import { BeneficiaryType, VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { ComponentProps, ElementType, ReactNode } from 'react';
import { Avatar } from '../../../design-system/avatar/avatar/avatar';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { Tooltip } from '../../../design-system/tooltip/tooltip';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { cn } from '../../../utils/cn';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getCurrencyFlag } from '../../../utils/get-currency-flag';

type AccountDetailsConfig =
  | {
      beneficiaryType: BeneficiaryType.IBAN;
      iban: string;
    }
  | {
      beneficiaryType: BeneficiaryType.SCAN;
      accountNumber: string;
      sortCode: string;
    };

type BeneficiaryCardProps = {
  name: string;
  currency: SupportedCountryCurrency;
  accountDetails: AccountDetailsConfig;
  // Prevents the border from being displayed when the component is used in a select
  isSelectOption?: boolean;
  latestPayeeCheck: VerifyPayeeCheckResponseCode | undefined;
};

export const BeneficiaryCard = ({
  name,
  currency,
  accountDetails,
  isSelectOption = false,
  latestPayeeCheck,
}: BeneficiaryCardProps) => {
  const { isVopPaymentsLive } = useFlags();

  const currencyFlag = getCurrencyFlag(currency);
  const accountDetailsText = getAccountDetailsText({ accountDetails });

  const latestPayeeCheckBadge = getLatestPayeeCheckBadge(latestPayeeCheck);
  const { isVisible: isPayeeCheckBadgeVisible } = latestPayeeCheckBadge;

  return (
    <Inline
      space={3}
      className={cn('w-full rounded-lg border border-seapoint-gray-border bg-white px-5 py-4', {
        'rounded-none border-none bg-transparent p-0': isSelectOption,
      })}
    >
      <Avatar name={name} size="sm" />

      <Stack space={1} className="min-w-0 flex-1">
        <Inline
          space={2}
          className={cn('w-full flex-wrap sm:flex-nowrap', {
            'flex-nowrap': isPayeeCheckBadgeVisible && !latestPayeeCheckBadge.text,
          })}
        >
          <h3 className="w-max-content truncate text-nowrap text-start text-sm font-medium text-black">{name}</h3>

          {isVopPaymentsLive && isPayeeCheckBadgeVisible && (
            <PayeeCheckBadgeWrapper tooltipConfig={latestPayeeCheckBadge.tooltipConfig}>
              <Inline space={1} wrap="nowrap">
                <div className="h-4 w-4">{latestPayeeCheckBadge.icon}</div>

                {latestPayeeCheckBadge.text && (
                  <p
                    className={cn(
                      'text-nowrap text-xs font-medium leading-5 tracking-tight',
                      latestPayeeCheckBadge.textClassName,
                    )}
                  >
                    {latestPayeeCheckBadge.text}
                  </p>
                )}
              </Inline>
            </PayeeCheckBadgeWrapper>
          )}
        </Inline>

        <p className="w-full truncate text-start text-xs font-normal text-seapoint-gray-text">{accountDetailsText}</p>
      </Stack>

      <div className="h-6 w-6 overflow-clip rounded-full">
        <img src={currencyFlag} alt="" loading="eager" fetchPriority="high" className="h-full w-full" />
      </div>
    </Inline>
  );
};

type GetAccountDetailsTextArgs = {
  accountDetails: AccountDetailsConfig;
};

const getAccountDetailsText = ({ accountDetails }: GetAccountDetailsTextArgs) => {
  const { beneficiaryType } = accountDetails;

  switch (beneficiaryType) {
    case BeneficiaryType.IBAN:
      return accountDetails.iban;
    case BeneficiaryType.SCAN:
      return `${accountDetails.accountNumber} • ${accountDetails.sortCode}`;
    default:
      return exhaustiveCheck(beneficiaryType);
  }
};

const PAYEE_CHECK_BADGE_COLORS = {
  SUCCESS: {
    hex: '#169B62',
    textClassName: 'text-seapoint-green-badge',
  },
  WARNING: {
    hex: '#FF771D',
    textClassName: 'text-seapoint-orange',
  },
  ERROR: {
    hex: '#FF531D',
    textClassName: 'text-seapoint-red',
  },
};

type TooltipConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      text: string;
    };

type LatestPayeeCheckBadgeConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      icon: ReactNode;
      text?: string;
      textClassName?: string;
      tooltipConfig: TooltipConfig;
    };

const getLatestPayeeCheckBadge = (
  latestPayeeCheck: ComponentProps<typeof BeneficiaryCard>['latestPayeeCheck'],
): LatestPayeeCheckBadgeConfig => {
  if (!latestPayeeCheck) {
    return {
      isVisible: false,
    };
  }

  switch (latestPayeeCheck) {
    case VerifyPayeeCheckResponseCode.MATCHED: {
      const severity = 'SUCCESS';

      return {
        isVisible: true,
        icon: createBadgeIcon(CircleCheck, PAYEE_CHECK_BADGE_COLORS[severity].hex),
        tooltipConfig: {
          isVisible: true,
          text: 'Details fully match',
        },
      };
    }
    case VerifyPayeeCheckResponseCode.CLOSE_MATCH: {
      const severity = 'WARNING';

      return {
        isVisible: true,
        icon: createBadgeIcon(CircleAlert, PAYEE_CHECK_BADGE_COLORS[severity].hex),
        text: 'Close match',
        textClassName: PAYEE_CHECK_BADGE_COLORS[severity].textClassName,
        tooltipConfig: {
          isVisible: false,
        },
      };
    }
    case VerifyPayeeCheckResponseCode.NO_MATCH: {
      const severity = 'ERROR';

      return {
        isVisible: true,
        icon: createBadgeIcon(CircleAlert, PAYEE_CHECK_BADGE_COLORS[severity].hex),
        text: "Some details don't match",
        textClassName: PAYEE_CHECK_BADGE_COLORS[severity].textClassName,
        tooltipConfig: {
          isVisible: false,
        },
      };
    }
    case VerifyPayeeCheckResponseCode.CHECK_NOT_POSSIBLE:
    case VerifyPayeeCheckResponseCode.INVALID:
    case VerifyPayeeCheckResponseCode.GENERAL:
    case VerifyPayeeCheckResponseCode.INTERNAL_ERROR: {
      const severity = 'ERROR';

      return {
        isVisible: true,
        icon: createBadgeIcon(CircleAlert, PAYEE_CHECK_BADGE_COLORS[severity].hex),
        text: "Details couldn't be checked",
        textClassName: PAYEE_CHECK_BADGE_COLORS[severity].textClassName,
        tooltipConfig: {
          isVisible: false,
        },
      };
    }
    default:
      return exhaustiveCheck(latestPayeeCheck);
  }
};

const createBadgeIcon = (Icon: ElementType, color: string) => (
  <Icon color={color} strokeWidth={1.5} className="h-full w-full" />
);

type TooltipWrapperProps = {
  tooltipConfig: TooltipConfig;
  children: ReactNode;
};

const PayeeCheckBadgeWrapper = ({ tooltipConfig, children }: TooltipWrapperProps) => {
  if (!tooltipConfig.isVisible) {
    return children;
  }

  return (
    <Tooltip
      content={tooltipConfig.text}
      trigger={children}
      contentAlign="center"
      contentClassName="bg-black text-white text-[10px] p-2.5"
      arrowConfig={{ isVisible: true, className: 'bg-black fill-black' }}
    />
  );
};
